import { useSyncExternalStore } from 'react';

import {
	SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS,
	type SmartFolderRovoConnectorProvider,
} from './SmartFolderRovoConnectorProviders.ts';
import { getSyncErrorState, type SmartFolderErrorState } from './SmartFolderErrorConstants.ts';

const SMART_FOLDER_PROPERTIES_KEY = 'smartFolderProperties';
const DEFAULT_PROVIDER = SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS['google-drive'];

export type ContentPropertyNode = {
	readonly key?: string | null;
	readonly value?: unknown;
} | null;

type SmartFolderContentProperties = {
	readonly appName?: unknown;
	readonly containerApp?: unknown;
	readonly containerAri?: unknown;
	readonly lastSyncAttemptedAt?: unknown;
	readonly lastSynced?: unknown;
	readonly paused?: unknown;
	readonly provider?: unknown;
	readonly sourceName?: unknown;
	readonly sourcePath?: unknown;
	readonly sourceUrl?: unknown;
	readonly syncErrorType?: unknown;
	readonly value?: unknown;
};

export type SmartFolderQuickActionSource = {
	appName: string;
	provider: SmartFolderRovoConnectorProvider;
};

export type SmartFolderSourceContext = SmartFolderQuickActionSource & {
	lastSyncAttemptedAt?: string;
	lastSynced?: string;
	sourceName: string;
	sourcePath?: string;
	sourceUrl?: string;
	syncErrorType?: string;
};

type UseSmartFolderQuickActionSourceOptions = {
	skip?: boolean;
};

const normalizeProviderName = (name: string): string =>
	name
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');

const providerByContainerApp = {
	[normalizeProviderName('Google Drive')]: 'google-drive',
	[normalizeProviderName('google-drive')]: 'google-drive',
	[normalizeProviderName('google-drive-connector')]: 'google-drive',
	[normalizeProviderName('smartlinks-connector-gdrive')]: 'google-drive',
	[normalizeProviderName('smartlinks-connector-gdrive-rsl')]: 'google-drive',
	[normalizeProviderName('lite-connector-gdrive')]: 'google-drive',
	[normalizeProviderName('SharePoint')]: 'sharepoint',
	[normalizeProviderName('sharepoint')]: 'sharepoint',
	[normalizeProviderName('sharepoint-connector')]: 'sharepoint',
	[normalizeProviderName('smartlinks-connector-sharepoint')]: 'sharepoint',
	[normalizeProviderName('ai-3p-connector:sharepoint-domain')]: 'sharepoint',
	[normalizeProviderName('OneDrive')]: 'onedrive',
	[normalizeProviderName('onedrive')]: 'onedrive',
	[normalizeProviderName('smartlinks-connector-onedrive')]: 'onedrive',
	[normalizeProviderName('onedrive-connector')]: 'onedrive',
} satisfies Record<string, SmartFolderRovoConnectorProvider>;

const rememberedSources = new Map<string, SmartFolderQuickActionSource>();
const subscribers = new Set<() => void>();

const subscribe = (callback: () => void) => {
	subscribers.add(callback);

	return () => {
		subscribers.delete(callback);
	};
};

const skipSubscribe = () => () => {};
const getSkippedRememberedSource = () => undefined;

const notifySubscribers = () => {
	subscribers.forEach((callback) => callback());
};

const getSourceFromProvider = (
	provider?: SmartFolderRovoConnectorProvider,
): SmartFolderQuickActionSource | undefined => {
	const providerConfig = provider ? SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS[provider] : undefined;

	return provider && providerConfig
		? {
				appName: providerConfig.displayName,
				provider,
			}
		: undefined;
};

const getProviderFromContainerAri = (
	containerAri?: string,
): SmartFolderRovoConnectorProvider | undefined => {
	if (!containerAri) {
		return undefined;
	}

	const normalizedContainerAri = containerAri.toLowerCase();

	if (
		normalizedContainerAri.includes('microsoft.sharepoint') ||
		normalizedContainerAri.includes(':sharepoint::')
	) {
		return 'sharepoint';
	}

	if (
		normalizedContainerAri.includes('microsoft.onedrive') ||
		normalizedContainerAri.includes(':onedrive::')
	) {
		return 'onedrive';
	}

	if (
		normalizedContainerAri.includes('google.google-drive') ||
		normalizedContainerAri.includes(':drive::')
	) {
		return 'google-drive';
	}

	return undefined;
};

const getSourceFromContainerApp = (
	containerApp?: string,
): SmartFolderQuickActionSource | undefined =>
	getSourceFromProvider(
		containerApp ? providerByContainerApp[normalizeProviderName(containerApp)] : undefined,
	);

const parseSmartFolderContentProperties = (
	propertyValue: unknown,
): SmartFolderContentProperties | undefined => {
	let parsedValue = propertyValue;

	try {
		for (let unwrapCount = 0; unwrapCount < 3; unwrapCount += 1) {
			if (typeof parsedValue === 'string') {
				parsedValue = JSON.parse(parsedValue) as SmartFolderContentProperties | string;
				continue;
			}

			if (typeof parsedValue !== 'object' || parsedValue === null) {
				return undefined;
			}

			const contentProperties = parsedValue as SmartFolderContentProperties;
			if (
				typeof contentProperties.value === 'string' ||
				typeof contentProperties.value === 'object'
			) {
				parsedValue = contentProperties.value;
				continue;
			}

			return contentProperties;
		}
	} catch {
		return undefined;
	}

	return typeof parsedValue === 'object' && parsedValue !== null
		? (parsedValue as SmartFolderContentProperties)
		: undefined;
};

const getOptionalTrimmedString = (value: unknown): string | undefined => {
	if (typeof value !== 'string') {
		return undefined;
	}

	const trimmedValue = value.trim();
	return trimmedValue.length > 0 ? trimmedValue : undefined;
};

const getSafeHttpUrl = (value?: string): string | undefined => {
	if (!value) {
		return undefined;
	}

	try {
		const parsedUrl = new URL(value);
		return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
			? parsedUrl.href
			: undefined;
	} catch {
		return undefined;
	}
};

const getSourceFromParsedValue = (
	parsedValue: SmartFolderContentProperties,
): SmartFolderQuickActionSource | undefined => {
	const containerApp =
		typeof parsedValue.containerApp === 'string'
			? parsedValue.containerApp
			: typeof parsedValue.appName === 'string'
				? parsedValue.appName
				: undefined;
	const sourceFromContainerApp = getSourceFromContainerApp(containerApp);
	if (sourceFromContainerApp) {
		return sourceFromContainerApp;
	}

	const providerName = typeof parsedValue.provider === 'string' ? parsedValue.provider : undefined;
	const sourceFromProvider = getSourceFromContainerApp(providerName);
	if (sourceFromProvider) {
		return sourceFromProvider;
	}

	const containerAri =
		typeof parsedValue.containerAri === 'string' ? parsedValue.containerAri : undefined;
	return getSourceFromProvider(getProviderFromContainerAri(containerAri));
};

const getParsedSmartFolderContentProperties = (
	properties?: readonly ContentPropertyNode[] | null,
): SmartFolderContentProperties | undefined => {
	const smartFolderProperties = properties?.find(
		(property) => property?.key === SMART_FOLDER_PROPERTIES_KEY,
	);

	return smartFolderProperties?.value
		? parseSmartFolderContentProperties(smartFolderProperties.value)
		: undefined;
};

const getSourceFromProperties = (
	properties?: readonly ContentPropertyNode[] | null,
): SmartFolderQuickActionSource | undefined => {
	const parsedValue = getParsedSmartFolderContentProperties(properties);
	return parsedValue ? getSourceFromParsedValue(parsedValue) : undefined;
};

const getDefaultQuickActionSource = (): SmartFolderQuickActionSource => ({
	appName: DEFAULT_PROVIDER.displayName,
	provider: DEFAULT_PROVIDER.provider,
});

const buildSmartFolderSourceContext = (
	source: SmartFolderQuickActionSource,
	parsedValue?: SmartFolderContentProperties,
): SmartFolderSourceContext => {
	const sourcePath = getOptionalTrimmedString(parsedValue?.sourcePath);
	const sourceName =
		getOptionalTrimmedString(parsedValue?.sourceName) ?? sourcePath ?? source.appName;

	return {
		...source,
		sourceName,
		sourcePath,
		sourceUrl: getSafeHttpUrl(getOptionalTrimmedString(parsedValue?.sourceUrl)),
		lastSynced: getOptionalTrimmedString(parsedValue?.lastSynced),
		lastSyncAttemptedAt: getOptionalTrimmedString(parsedValue?.lastSyncAttemptedAt),
		syncErrorType: getOptionalTrimmedString(parsedValue?.syncErrorType),
	};
};

export const getIsSmartFolderSyncPaused = (
	properties?: readonly ContentPropertyNode[] | null,
): boolean => getParsedSmartFolderContentProperties(properties)?.paused === true;

export const getSmartFolderLastSynced = (
	properties?: readonly ContentPropertyNode[] | null,
): string | null => {
	const smartFolderProperties = properties?.find(
		(property) => property?.key === SMART_FOLDER_PROPERTIES_KEY,
	);
	const lastSynced = parseSmartFolderContentProperties(smartFolderProperties?.value)?.lastSynced;

	return typeof lastSynced === 'string' ? lastSynced : null;
};

export const getSmartFolderSyncErrorState = (
	properties?: readonly ContentPropertyNode[] | null,
): SmartFolderErrorState | null => {
	const smartFolderProperties = properties?.find(
		(property) => property?.key === SMART_FOLDER_PROPERTIES_KEY,
	);
	const syncErrorType = parseSmartFolderContentProperties(
		smartFolderProperties?.value,
	)?.syncErrorType;

	return typeof syncErrorType === 'string' ? getSyncErrorState(syncErrorType) : null;
};

export const rememberSmartFolderQuickActionSource = ({
	containerApp,
	folderId,
}: {
	containerApp?: string;
	folderId: string;
}): void => {
	const source = getSourceFromContainerApp(containerApp);
	if (!source) {
		return;
	}

	rememberedSources.set(folderId, source);
	notifySubscribers();
};

export const getSmartFolderQuickActionSource = (
	properties?: readonly ContentPropertyNode[] | null,
): SmartFolderQuickActionSource =>
	getSourceFromProperties(properties) ?? getDefaultQuickActionSource();

export const getSmartFolderSourceContext = (
	properties?: readonly ContentPropertyNode[] | null,
): SmartFolderSourceContext =>
	buildSmartFolderSourceContext(
		getSmartFolderQuickActionSource(properties),
		getParsedSmartFolderContentProperties(properties),
	);

export const useSmartFolderQuickActionSource = (
	folderId: string,
	properties?: readonly ContentPropertyNode[] | null,
	options: UseSmartFolderQuickActionSourceOptions = {},
): SmartFolderQuickActionSource => {
	const { skip = false } = options;
	const rememberedSource = useSyncExternalStore(
		skip ? skipSubscribe : subscribe,
		skip ? getSkippedRememberedSource : () => rememberedSources.get(folderId),
		getSkippedRememberedSource,
	);

	if (skip) {
		return getDefaultQuickActionSource();
	}

	return getSourceFromProperties(properties) ?? rememberedSource ?? getDefaultQuickActionSource();
};

export const useSmartFolderSourceContext = (
	folderId: string,
	properties?: readonly ContentPropertyNode[] | null,
	options: UseSmartFolderQuickActionSourceOptions = {},
): SmartFolderSourceContext => {
	const source = useSmartFolderQuickActionSource(folderId, properties, options);
	return buildSmartFolderSourceContext(source, getParsedSmartFolderContentProperties(properties));
};
